import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';

import type {
    InputMaybe,
    InvoicingPeriod,
    AutomaticInvoicingOccurrence,
    AutomaticInvoicingConfigInput,
} from '@famly/vet_api-types';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {useGenericDialog} from '@famly/mf_modals-dialogs_context';

import {useAutomaticInvoicingConfig} from './use-automatic-invoicing-config';
import {useSetAutomaticInvoicingConfig} from './use-set-automatic-invoicing-config';
import {getOrNull, monthlyInvoicingDueInput, weeklyInvoicingDueInput} from '../helpers';
import {WeeklyInvoicingDue, type BiWeeklyState, type MonthlyState, type WeeklyState} from '../types';

export const shouldShowDefaultBillingFrequency = (isWeekly, isBiWeekly, isMonthly, isOnUsCorePackage) => {
    const enabledSchedules = enabled => !enabled;

    return (
        (([isWeekly, isBiWeekly, isMonthly].filter(enabledSchedules).length <= 1 && isOnUsCorePackage) ||
            !isOnUsCorePackage) ??
        true
    );
};

export const shouldShowDefaultBillingFrequencyToggle = isOnUsCorePackage => !isOnUsCorePackage;

export const useAutomaticInvoicingData = (
    /**
     * useParams can in some cases return null for institutionId
     * For example in a portal modal or a page where there is no institutionId in the URl
     * For this reason we let consumers pass a siteId argument
     */
    siteId?: string,
) => {
    // MARK: - States
    const [monthly, setMonthly] = useState<MonthlyState>({
        isEnabled: false,
        billingDate: null,
        billingExactDay: null,
        invoicingDue: null,
        invoicingDueExactDay: null,
        invoicingDueDaysAfterBilling: null,
    });

    const [biweekly, setBiweekly] = useState<BiWeeklyState>({
        isEnabled: false,
        billingDate: null,
        invoicingDue: null,
        invoicingDueDaysAfterBilling: null,
    });

    const [weekly, setWeekly] = useState<WeeklyState>({
        isEnabled: false,
        billingDate: null,
        invoicingDue: null,
        invoicingDueDaysAfterBilling: null,
    });

    const [invoicePeriod, setInvoicePeriod] = useState<InputMaybe<InvoicingPeriod>>(null);
    const [defaultFrequency, setDefaultFrequency] = useState<InputMaybe<AutomaticInvoicingOccurrence>>(null);
    const [defaultFrequencyOptions, setDefaultFrequencyOptions] = useState<Array<{value: string; label: string}>>([]);
    const [defaultFrequencyToNewBillPayers, setDefaultFrequencyToNewBillPayers] = useState<boolean>(false);
    const [daysUntilPaymentDeadline, setDaysUntilPaymentDeadline] = useState<string | null>(null);
    const [sendInvoicesAutomatically, setSendInvoicesAutomatically] = useState<boolean>(true);

    // MARK: - Hooks
    const {institutionId} = useParams<{institutionId: string}>();
    const dispatch = useDispatch();

    const {automaticInvoicingConfig, isOnUsCorePackage} = useAutomaticInvoicingConfig(siteId ?? institutionId);
    const {setAutomationInvoicingConfig} = useSetAutomaticInvoicingConfig(siteId ?? institutionId);

    const {showDialog, hideDialog} = useGenericDialog();

    // MARK: - Constants
    const configurationInheritance = automaticInvoicingConfig?.configurationInheritance;
    const isReadOnly = configurationInheritance ? !configurationInheritance.canBeConfigured : false;

    const monthlyValues = React.useMemo(
        () =>
            (monthly.billingDate === 'EXACT_DAY_OF_MONTH' && hasValue(monthly.billingExactDay)) ||
            (hasValue(monthly.billingDate) && monthly.billingDate !== 'EXACT_DAY_OF_MONTH'),
        [monthly.billingExactDay, monthly.billingDate],
    );

    const biWeeklyNextInvoiceDate = React.useMemo(() => {
        return automaticInvoicingConfig?.biweekly?.nextInvoicingDate
            ? moment(automaticInvoicingConfig?.biweekly?.nextInvoicingDate)
            : undefined;
    }, [automaticInvoicingConfig?.biweekly?.nextInvoicingDate]);

    const isDisablingState = hasValue(invoicePeriod) && !monthly.isEnabled && !weekly.isEnabled && !biweekly.isEnabled;
    const isInitialConfiguringState =
        hasValue(invoicePeriod) &&
        ((monthly.isEnabled && monthlyValues) ||
            (weekly.isEnabled && hasValue(weekly.billingDate)) ||
            (biweekly.isEnabled && hasValue(biweekly.billingDate)));

    const isValid = React.useMemo(
        () => isDisablingState || isInitialConfiguringState,
        [isDisablingState, isInitialConfiguringState],
    );

    // MARK: - Effects
    React.useEffect(() => {
        if ((monthly.isEnabled || biweekly.isEnabled || weekly.isEnabled) && !hasValue(invoicePeriod)) {
            // default invoice period value when an option is selected
            setInvoicePeriod('NEXT');
        }
    }, [invoicePeriod, biweekly.isEnabled, monthly.isEnabled, weekly.isEnabled]);

    React.useEffect(() => {
        if (!defaultFrequencyToNewBillPayers) {
            setDefaultFrequency(null);
        }
    }, [defaultFrequencyToNewBillPayers, setDefaultFrequency]);

    React.useEffect(() => {
        if (hasValue(defaultFrequency)) {
            setDefaultFrequencyToNewBillPayers(true);
        }
        const options: Array<{value: string; label: string}> = [];
        if (monthly.isEnabled && hasValue(monthly.billingDate)) {
            options.push({
                value: 'MONTHLY',
                label: i18next.t('automaticInvoicing.configuration.monthly'),
            });
        }

        if (biweekly.isEnabled && hasValue(biweekly.billingDate)) {
            options.push({
                value: 'BIWEEKLY',
                label: i18next.t('automaticInvoicing.configuration.biweekly'),
            });
        }

        if (weekly.isEnabled && hasValue(weekly.billingDate)) {
            options.push({
                value: 'WEEKLY',
                label: i18next.t('automaticInvoicing.configuration.weekly'),
            });
        }

        setDefaultFrequencyOptions(options);
    }, [
        defaultFrequency,
        monthly.isEnabled,
        monthly.billingDate,
        biweekly.isEnabled,
        biweekly.billingDate,
        weekly.isEnabled,
        weekly.billingDate,
    ]);

    const toggleOption = (optionType: AutomaticInvoicingOccurrence) => (isToggled: boolean) => {
        if (!isToggled && defaultFrequency === optionType) {
            showDialog({
                title: i18next.t('automaticInvoicing.configuration.dialogTitle'),
                confirmText: i18next.t('automaticInvoicing.configuration.okText'),
                description: i18next.t('automaticInvoicing.configuration.dialogDescription'),
                'data-e2e-id': 'automatic-invoicing-dialog',
                onConfirm: () => {
                    setDefaultFrequency(null);
                    setDefaultFrequencyToNewBillPayers(false);
                    hideDialog();
                },
            });
        }

        switch (optionType) {
            case 'MONTHLY':
                setMonthly(prev => ({...prev, isEnabled: isToggled}));
                return;
            case 'BIWEEKLY':
                setBiweekly(prev => ({...prev, isEnabled: isToggled}));
                return;
            case 'WEEKLY':
                setWeekly(prev => ({...prev, isEnabled: isToggled}));
                return;
            default:
                throw new Error('Invalid Automatic Invoicing Occurrence Type');
        }
    };

    const initialConfig: AutomaticInvoicingConfigInput = React.useMemo(
        () =>
            automaticInvoicingConfig
                ? {
                      invoicingPeriod: automaticInvoicingConfig.invoicingPeriod,
                      monthly: automaticInvoicingConfig.monthly
                          ? {
                                option: automaticInvoicingConfig.monthly.option,
                                exactDay: automaticInvoicingConfig.monthly.exactDay,
                                invoicingDue: automaticInvoicingConfig.monthly.invoicingDue,
                            }
                          : null,
                      biweekly: automaticInvoicingConfig.biweekly
                          ? {
                                startDate: automaticInvoicingConfig.biweekly.startDate,
                                nextInvoicingDate: automaticInvoicingConfig.biweekly.nextInvoicingDate,
                                invoicingDue: automaticInvoicingConfig.biweekly.invoicingDue,
                            }
                          : null,
                      weekly: automaticInvoicingConfig.weekly
                          ? {
                                exactDay: automaticInvoicingConfig.weekly.exactDay,
                                invoicingDue: automaticInvoicingConfig.weekly.invoicingDue,
                            }
                          : null,
                      assignToNewBillPayers: automaticInvoicingConfig.assignToNewBillPayers
                          ? {
                                occurrence: automaticInvoicingConfig.assignToNewBillPayers.occurrence,
                            }
                          : null,
                      daysUntilPaymentDeadline: automaticInvoicingConfig.daysUntilPaymentDeadline,
                      sendInvoicesAutomatically: automaticInvoicingConfig.sendInvoicesAutomatically,
                  }
                : {},
        [automaticInvoicingConfig],
    );

    const initializeMonthlyState = React.useCallback(() => {
        const invoicingDue = automaticInvoicingConfig?.monthly?.invoicingDue;

        const invoicingDueExactDay =
            invoicingDue?.__typename === 'DayOfMonthDue' ? invoicingDue?.exactDayOfMonth.toString() : null;

        const invoicingDueDaysAfterBilling =
            invoicingDue?.__typename === 'MonthlyRelativeToBillingDateDue'
                ? invoicingDue?.daysAfterSendingInvoice.toString()
                : null;

        setMonthly({
            isEnabled: hasValue(automaticInvoicingConfig?.monthly?.option),
            billingDate: getOrNull(automaticInvoicingConfig?.monthly?.option),
            billingExactDay: getOrNull(automaticInvoicingConfig?.monthly?.exactDay?.toString()),
            invoicingDue: getOrNull(invoicingDue?.type),
            invoicingDueExactDay,
            invoicingDueDaysAfterBilling,
        });
    }, [automaticInvoicingConfig?.monthly]);

    const initializeBiWeeklyState = React.useCallback(() => {
        const invoicingDue = automaticInvoicingConfig?.biweekly?.invoicingDue;

        const invoicingDueDaysAfterBilling =
            invoicingDue?.__typename === 'WeeklyRelativeToBillingDateDue'
                ? invoicingDue?.daysAfterSendingInvoice.toString()
                : null;

        const billingDate = automaticInvoicingConfig?.biweekly?.startDate
            ? moment(automaticInvoicingConfig.biweekly.startDate)
            : null;

        let invoicingDueOption: string | null = null;
        if (invoicingDue) {
            if (invoicingDue.__typename === 'DayOfWeekDue') {
                invoicingDueOption = invoicingDue.exactDayOfWeek;
            } else {
                invoicingDueOption = WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE;
            }
        }

        setBiweekly({
            isEnabled: hasValue(automaticInvoicingConfig?.biweekly?.startDate),
            invoicingDue: invoicingDueOption,
            billingDate,
            invoicingDueDaysAfterBilling,
        });
    }, [automaticInvoicingConfig?.biweekly]);

    const initializeWeeklyState = React.useCallback(() => {
        const invoicingDue = automaticInvoicingConfig?.weekly?.invoicingDue;

        const invoicingDueDaysAfterBilling =
            invoicingDue?.__typename === 'WeeklyRelativeToBillingDateDue'
                ? invoicingDue?.daysAfterSendingInvoice.toString()
                : null;

        let invoicingDueOption: string | null = null;
        if (invoicingDue) {
            if (invoicingDue.__typename === 'DayOfWeekDue') {
                invoicingDueOption = invoicingDue.exactDayOfWeek;
            } else {
                invoicingDueOption = WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE;
            }
        }

        setWeekly({
            isEnabled: hasValue(automaticInvoicingConfig?.weekly?.exactDay),
            billingDate: getOrNull(automaticInvoicingConfig?.weekly?.exactDay),
            invoicingDue: invoicingDueOption,
            invoicingDueDaysAfterBilling,
        });
    }, [automaticInvoicingConfig?.weekly]);

    const handleSubmit = React.useCallback(async () => {
        const configObj: AutomaticInvoicingConfigInput = {
            invoicingPeriod: invoicePeriod,
            sendInvoicesAutomatically,
        };

        const formattedMonthlyInvoicing = monthlyInvoicingDueInput(
            monthly.invoicingDue,
            monthly.invoicingDueExactDay,
            monthly.invoicingDueDaysAfterBilling,
        );

        if (hasValue(monthly.billingDate)) {
            configObj.monthly = {
                option: monthly.billingDate,
                ...(hasValue(monthly.billingExactDay) ? {exactDay: Number(monthly.billingExactDay)} : {}),
                ...(formattedMonthlyInvoicing ? {invoicingDue: formattedMonthlyInvoicing} : {}),
            };
        }

        const formattedBiWeeklyInvoicing = hasValue(biweekly.invoicingDue)
            ? weeklyInvoicingDueInput(biweekly.invoicingDue, Number(biweekly.invoicingDueDaysAfterBilling))
            : undefined;

        if (hasValue(biweekly.billingDate)) {
            configObj.biweekly = {
                startDate: biweekly.billingDate.format('YYYY-MM-DD'),
                ...(formattedBiWeeklyInvoicing ? {invoicingDue: formattedBiWeeklyInvoicing} : {}),
            };
        }

        const formattedWeeklyInvoicing = hasValue(weekly.invoicingDue)
            ? weeklyInvoicingDueInput(weekly.invoicingDue, Number(weekly.invoicingDueDaysAfterBilling))
            : undefined;

        if (hasValue(weekly.billingDate)) {
            configObj.weekly = {
                exactDay: weekly.billingDate,
                ...(formattedWeeklyInvoicing ? {invoicingDue: formattedWeeklyInvoicing} : {}),
            };
        }

        if (hasValue(defaultFrequency)) {
            configObj.assignToNewBillPayers = {
                occurrence: defaultFrequency,
            };
        }
        if (isDisablingState) {
            configObj.invoicingPeriod = null;
            configObj.monthly = null;
            configObj.weekly = null;
            configObj.biweekly = null;
        }

        const {data, errors} = await setAutomationInvoicingConfig({
            variables: {siteSetId: siteId ?? institutionId, config: configObj},
        });

        if (!data || errors) {
            dispatch(GlobalEventActions.updateError.action(i18next.t('somethingWentWrong')));
            return;
        }

        dispatch(GlobalEventActions.updateSuccess.action(i18next.t('automaticInvoicing.configuration.successMessage')));
    }, [
        invoicePeriod,
        sendInvoicesAutomatically,
        monthly.invoicingDue,
        monthly.invoicingDueExactDay,
        monthly.invoicingDueDaysAfterBilling,
        monthly.billingDate,
        monthly.billingExactDay,
        biweekly.invoicingDue,
        biweekly.invoicingDueDaysAfterBilling,
        biweekly.billingDate,
        weekly.invoicingDue,
        weekly.invoicingDueDaysAfterBilling,
        weekly.billingDate,
        defaultFrequency,
        isDisablingState,
        setAutomationInvoicingConfig,
        siteId,
        institutionId,
        dispatch,
    ]);

    React.useEffect(() => {
        setInvoicePeriod(getOrNull(automaticInvoicingConfig?.invoicingPeriod));
        setDefaultFrequency(getOrNull(automaticInvoicingConfig?.assignToNewBillPayers?.occurrence));
        setSendInvoicesAutomatically(automaticInvoicingConfig?.sendInvoicesAutomatically ?? true);

        initializeMonthlyState();
        initializeBiWeeklyState();
        initializeWeeklyState();
    }, [automaticInvoicingConfig, initializeBiWeeklyState, initializeMonthlyState, initializeWeeklyState]);

    return {
        monthly,
        biweekly,
        biWeeklyNextInvoiceDate,
        weekly,
        setMonthly,
        setBiweekly,
        setWeekly,
        enableMonthly: toggleOption('MONTHLY'),
        enableBiWeekly: toggleOption('BIWEEKLY'),
        enableWeekly: toggleOption('WEEKLY'),

        invoicePeriod,
        defaultFrequency,
        defaultFrequencyOptions,
        defaultFrequencyToNewBillPayers,
        isValid,
        isReadOnly,
        daysUntilPaymentDeadline,
        sendInvoicesAutomatically,
        shouldShowDefaultBillingFrequency: shouldShowDefaultBillingFrequency(
            weekly.isEnabled,
            biweekly.isEnabled,
            monthly.isEnabled,
            isOnUsCorePackage,
        ),
        shouldShowDefaultBillingFrequencyToggle: shouldShowDefaultBillingFrequencyToggle(isOnUsCorePackage),
        setInvoicePeriod,
        setDefaultFrequency,
        setDefaultFrequencyToNewBillPayers,
        setDaysUntilPaymentDeadline,
        setSendInvoicesAutomatically,
        handleSubmit,
        initialConfig,
    };
};
